// useVideoCommand Hook - 2ECOMP Fleet Management
// Sends JT1078 video commands via Traccar API

import { useState, useCallback } from 'react';
import { useCameraServer } from './useCameraServer';

/**
 * Command types supported by Traccar
 */
export const VIDEO_COMMAND_TYPE = {
  REQUEST_VIDEO: 'requestVideo',
  STOP_VIDEO: 'stopVideo',
  REQUEST_VIDEO_PLAYBACK: 'requestVideoPlayback',
  CONTROL_VIDEO_PLAYBACK: 'controlVideoPlayback',
  QUERY_VIDEO_RESOURCES: 'queryVideoResources',
  CUSTOM: 'custom',
};

/**
 * Media types for JT1078
 */
export const MEDIA_TYPE = {
  AV: 0,           // Audio + Video
  VIDEO_ONLY: 1,   // Video only
  DUAL_TALK: 2,    // Two-way audio
  LISTEN: 3,       // Listen only
  BROADCAST: 4,    // Broadcast
};

/**
 * Stream types for JT1078
 */
export const STREAM_TYPE = {
  MAIN: 0,         // Main stream (HD)
  SUB: 1,          // Sub stream (lower quality)
};

/**
 * Playback control actions for JT1078 0x9202
 */
export const PLAYBACK_CONTROL = {
  START: 0,
  PAUSE: 1,
  END: 2,
  FAST_FORWARD: 3,
  REWIND: 4,
  DRAG: 5,
  KEYFRAME: 6,
};

/**
 * Speed multiplier values for JT1078 playback
 */
export const SPEED_MULTIPLIER = {
  INVALID: 0,
  X1: 1,
  X2: 2,
  X4: 3,
  X8: 4,
  X16: 5,
};

/**
 * Build JT808 0x9101 frame for custom command fallback
 */
const buildVideoRequestFrame = (deviceImei, channel, streamType, mediaType, serverIp, serverPort) => {
  const calculateChecksum = (data) => {
    let checksum = 0;
    for (const b of data) {
      checksum ^= b;
    }
    return checksum;
  };

  const escape7e = (data) => {
    const result = [];
    for (const b of data) {
      if (b === 0x7e) {
        result.push(0x7d, 0x02);
      } else if (b === 0x7d) {
        result.push(0x7d, 0x01);
      } else {
        result.push(b);
      }
    }
    return result;
  };

  // Device ID (pad to 12 digits BCD)
  const deviceIdPadded = deviceImei.toString().padStart(12, '0');
  const deviceIdBcd = [];
  for (let i = 0; i < 12; i += 2) {
    deviceIdBcd.push(parseInt(deviceIdPadded.substr(i, 2), 16));
  }

  // Build body payload
  const ip = serverIp;
  const tcpPort = serverPort;
  const udpPort = 0;

  const body = [ip.length];
  for (let i = 0; i < ip.length; i++) {
    body.push(ip.charCodeAt(i));
  }
  body.push((tcpPort >> 8) & 0xff, tcpPort & 0xff);
  body.push((udpPort >> 8) & 0xff, udpPort & 0xff);
  body.push(channel, mediaType, streamType);

  // Message properties (length in lower 10 bits)
  const msgProps = body.length & 0x03ff;

  // Serial number
  const serial = Math.floor(Math.random() * 65535);

  // Build frame
  const frame = [
    0x91, 0x01,                           // Message ID (0x9101)
    (msgProps >> 8) & 0xff, msgProps & 0xff, // Message properties
    ...deviceIdBcd,                       // Device ID (6 bytes BCD)
    (serial >> 8) & 0xff, serial & 0xff,  // Serial number
    ...body,                              // Body
  ];

  // Calculate checksum
  const checksum = calculateChecksum(frame);

  // Escape special bytes
  const escaped = escape7e([...frame, checksum]);

  // Build final frame with delimiters
  const finalFrame = [0x7e, ...escaped, 0x7e];

  // Convert to hex string
  return finalFrame.map((b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
};

/**
 * Custom hook for sending video commands via Traccar API
 *
 * @param {Object} options - Configuration options
 * @returns {Object} Command state and functions
 */
export const useVideoCommand = (options = {}) => {
  const {
    apiUrl = '/api',
    onSuccess = null,
    onError = null,
  } = options;

  const cameraServer = useCameraServer();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastResponse, setLastResponse] = useState(null);

  /**
   * Send command to device via Traccar API
   */
  const sendCommand = useCallback(async (deviceId, type, attributes = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiUrl}/commands/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          deviceId,
          type,
          attributes,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Command failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      setLastResponse(result);

      if (onSuccess) {
        onSuccess(result);
      }

      return result;
    } catch (err) {
      console.error('[VideoCommand] Error:', err);
      setError(err);

      if (onError) {
        onError(err);
      }

      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, onSuccess, onError]);

  /**
   * Request video stream from camera
   * Sends JT1078 0x9101 command
   */
  const requestVideo = useCallback(async (deviceId, options = {}) => {
    const {
      channel = 1,
      streamType = STREAM_TYPE.SUB,
      mediaType = MEDIA_TYPE.AV,
      server = cameraServer.ip,
      port = cameraServer.jt1078Port,
      useCustomFallback = true,
    } = options;

    try {
      // First, try the native requestVideo command
      return await sendCommand(deviceId, VIDEO_COMMAND_TYPE.REQUEST_VIDEO, {
        channel,
        streamType,
        mediaType,
        server,
        port,
      });
    } catch (err) {
      // If requestVideo is not supported, try custom command with raw frame
      if (useCustomFallback && err.message.includes('400')) {
        // Get device IMEI
        const deviceResponse = await fetch(`${apiUrl}/devices/${deviceId}`, {
          credentials: 'include',
        });

        if (!deviceResponse.ok) {
          throw new Error('Failed to get device info');
        }

        const device = await deviceResponse.json();
        const imei = device.uniqueId;

        // Build raw JT808 frame
        const frameHex = buildVideoRequestFrame(imei, channel, streamType, mediaType, cameraServer.ip, cameraServer.jt1078Port);

        return await sendCommand(deviceId, VIDEO_COMMAND_TYPE.CUSTOM, {
          data: frameHex,
        });
      }

      throw err;
    }
  }, [sendCommand, apiUrl, cameraServer]);

  /**
   * Stop video stream from camera
   * Sends JT1078 0x9102 command
   */
  const stopVideo = useCallback(async (deviceId, options = {}) => {
    const {
      channel = 1,
      streamType = STREAM_TYPE.MAIN,
    } = options;

    return await sendCommand(deviceId, VIDEO_COMMAND_TYPE.STOP_VIDEO, {
      channel,
      streamType,
    });
  }, [sendCommand]);

  /**
   * Query video resources on device SD card
   * Sends JT1078 0x9205 command
   */
  const queryVideoResources = useCallback(async (deviceId, options = {}) => {
    const {
      channel = 1,
      startTime,
      endTime,
      alarmFlag = 0,
      avResourceType = 0,
      streamType = 0,
      storageType = 0,
    } = options;

    return await sendCommand(deviceId, VIDEO_COMMAND_TYPE.QUERY_VIDEO_RESOURCES, {
      channel,
      startTime,
      endTime,
      alarmFlag,
      avResourceType,
      streamType,
      storageType,
    });
  }, [sendCommand]);

  /**
   * Request video playback from camera SD card
   * Sends JT1078 0x9201 command
   */
  const requestPlayback = useCallback(async (deviceId, options = {}) => {
    const {
      channel = 1,
      startTime,
      endTime,
      mediaType = MEDIA_TYPE.AV,
      streamType = STREAM_TYPE.MAIN,
      memoryType = 0,
      playbackMethod = 0,
      speedMultiplier = SPEED_MULTIPLIER.X1,
      server = cameraServer.ip,
      port = cameraServer.jt1078Port,
    } = options;

    return await sendCommand(deviceId, VIDEO_COMMAND_TYPE.REQUEST_VIDEO_PLAYBACK, {
      channel,
      startTime,
      endTime,
      mediaType,
      streamType,
      memoryType,
      playbackMethod,
      speedMultiplier,
      server,
      port,
    });
  }, [sendCommand, cameraServer]);

  /**
   * Control video playback (pause, resume, speed, seek, stop)
   * Sends JT1078 0x9202 command
   */
  const controlPlayback = useCallback(async (deviceId, options = {}) => {
    const {
      channel = 1,
      playbackControl = PLAYBACK_CONTROL.START,
      speedMultiplier = SPEED_MULTIPLIER.X1,
      dragPosition = null,
    } = options;

    return await sendCommand(deviceId, VIDEO_COMMAND_TYPE.CONTROL_VIDEO_PLAYBACK, {
      channel,
      playbackControl,
      speedMultiplier,
      ...(dragPosition ? { startTime: dragPosition } : {}),
    });
  }, [sendCommand]);

  /**
   * Stop video playback (convenience wrapper)
   */
  const stopPlayback = useCallback(async (deviceId, options = {}) => {
    const { channel = 1 } = options;
    return controlPlayback(deviceId, {
      channel,
      playbackControl: PLAYBACK_CONTROL.END,
    });
  }, [controlPlayback]);

  /**
   * Check supported command types for a device
   */
  const getSupportedCommands = useCallback(async (deviceId) => {
    try {
      const response = await fetch(`${apiUrl}/commands/types?deviceId=${deviceId}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to get command types');
      }

      return await response.json();
    } catch (err) {
      console.error('[VideoCommand] Error getting command types:', err);
      return [];
    }
  }, [apiUrl]);

  /**
   * Check if device supports video commands
   */
  const supportsVideoCommands = useCallback(async (deviceId) => {
    const commands = await getSupportedCommands(deviceId);
    const types = commands.map((c) => c.type);
    return {
      requestVideo: types.includes('requestVideo'),
      stopVideo: types.includes('stopVideo'),
      custom: types.includes('custom'),
    };
  }, [getSupportedCommands]);

  return {
    // State
    isLoading,
    error,
    lastResponse,

    // Actions
    requestVideo,
    stopVideo,
    queryVideoResources,
    requestPlayback,
    controlPlayback,
    stopPlayback,
    sendCommand,
    getSupportedCommands,
    supportsVideoCommands,

    // Constants
    MEDIA_TYPE,
    STREAM_TYPE,
    PLAYBACK_CONTROL,
    SPEED_MULTIPLIER,
  };
};

export default useVideoCommand;
